import {
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Td,
  Text,
  Tooltip,
  Tr,
  useBoolean,
  useDisclosure,
} from '@chakra-ui/react'
import { formatPersonFullName, InsuranceParamsGridItem, InsuranceParamsPropertyGridItem, InsuranceParamsVehicleGridItem, ParamsStateType } from 'BrokerTsLib/src/dtos'
import { Product, SelectionType } from 'BrokerTsLib/src/enums'
import { Link, useNavigate } from 'react-router-dom'
import { Progress, StepsProgress } from '../../components/StepsProgress'
import { useApp } from '../../hooks/useApp'
import { Edit, Note } from '../../icons'
import { logos } from '../../utils/companiesLogos'
import { formatDate } from '../../utils/formatDate'
import { Subject } from '../Subject'
import { useSelections } from '../../hooks'
import { UserLink } from '../shared/UserLink'

export const getConceptStatus = (status?: ParamsStateType) => {
  switch (status?.toLocaleLowerCase()) {
    case 'created':
      return { badgeColorScheme: 'blue' }
    case 'toSend':
      return { badgeColorScheme: 'orange' }
    case 'printed':
      return { badgeColorScheme: 'green' }
    case 'sent':
      return { badgeColorScheme: 'green' }
    case 'Contracted':
      return { badgeColorScheme: 'gray' }
    default:
      return { badgeColorScheme: 'yellow' }
  }
}

interface ConceptRowProps {
  allowSelect: boolean
  selectedRows: InsuranceParamsVehicleGridItem['insuranceParamsId'][]
  onRowSelect: (id: InsuranceParamsVehicleGridItem['insuranceParamsId']) => void
}

export const ConceptRow = ({ allowSelect, onRowSelect, selectedRows, item }: { item: InsuranceParamsGridItem } & ConceptRowProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { product, app } = useApp()
  const [buttonsVisible, setButtonsVisible] = useBoolean(false)
  const navigate = useNavigate()
  const { badgeColorScheme } = getConceptStatus(item.paramsState)
  const { insuranceParamsId: id } = item
  const isSelected = selectedRows.includes(id)

  const { data: dataTypNemovitosti } = useSelections(SelectionType.TypNemovitosti, { enabled: app == 'chaloupka' })

  let vehicleItem: InsuranceParamsVehicleGridItem | null = item as InsuranceParamsVehicleGridItem
  let propertyItem: InsuranceParamsPropertyGridItem | null = item as InsuranceParamsPropertyGridItem

  const logo = !!item.insurerId ? logos[item.insurerId!] : null
  const insureeName = formatPersonFullName(item.policyHolder)
  const subject = `${vehicleItem?.vehicle?.znacka ?? ''} ${vehicleItem?.vehicle?.model ?? ''}`.trim()

  let navigateTarget: string
  switch (product) {
    case Product.TravelInsurance:
      navigateTarget = `/cestovko/wizard/${item.step ?? 1}/${id}`
      break
    case Product.PropertyInsurance:
      navigateTarget = `/chaloupka/wizard/${item.step ?? 1}/${id}`
      break
    default:
      navigateTarget = `/carkulka/wizard/${item.step ?? 1}/${id}`

      if (item.type === 2)
        navigateTarget = `/carkulka/extra/wizard/${item.step ?? 1}/${id}`

      break
  }

  return (
    <Tr onMouseEnter={setButtonsVisible.on} onMouseLeave={setButtonsVisible.off}>
      <Td sx={{ display: allowSelect ? 'table-cell' : 'none' }}>
        <Checkbox
          isChecked={isSelected}
          onChange={() => onRowSelect(id)}
        />
      </Td>

      <Td>
        <Flex sx={{ columnGap: 4, rowGap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'grid', gap: 1 }} className="contract__id-progress">
            <Link to={navigateTarget} >
              <Text sx={{ color: 'primary.400', fontSize: 'xl', fontWeight: 'semibold' }}>{id}</Text>

              <StepsProgress progress={(item.step ?? 1) as Progress} maxSteps={item.type == 2 ? 4 : 5} />
            </Link>
          </Box>

          {item.note && (
            <Tooltip label={item.note} hasArrow placement="top" shouldWrapChildren>
              <Note color="secondary.500" />
            </Tooltip>
          )}
        </Flex>
      </Td>

      <Td>
        {item.dateCreated &&
          <Text sx={{ fontSize: 'sm' }}>
            {formatDate({ date: item.dateCreated })}
          </Text>

        }
        {item.startDate && (
          <Text sx={{ fontSize: 'sm', fontWeight: 'semibold' }}>
            {formatDate({ date: item.startDate })}
          </Text>
        )}
      </Td>

      <Td sx={{ px: 0 }}>
        {insureeName && <Avatar size="sm" colorScheme="primary" name={insureeName.trim()} />}
      </Td>

      <Td sx={{ pl: 0 }}>
        {insureeName && <Text sx={{ fontSize: 'sm' }}>{insureeName}</Text>}
        {!!subject && <Subject>{subject}</Subject>}
      </Td>

      {app == 'chaloupka' &&
        <Td>
          <p>{dataTypNemovitosti?.data.find(x => x.id == propertyItem?.data.realtyTypeId)?.description}</p>
        </Td>
      }

      <Td>
        {logo && <Image src={logo} alt={item.insurerFullName} width="64px" height="48px" />}
      </Td>

      <Td>
        {item.name && (
          <Text sx={{ color: 'gray.600', fontSize: 'sm' }} className="contract__name">
            {item.name}
          </Text>
        )}
        {!item.name && (
          <Text sx={{ color: 'gray.400', fontSize: 'xs' }} className="contract__name">Nezadáno</Text>
        )}
        {item.createdBy && (
          <UserLink firstName={item.createdBy.name} lastName={item.createdBy.surname} userId={item.createdBy.userId} sx={{ color: 'primary.500', fontSize: 'sm', fontWeight: 'semibold' }} />
        )}
      </Td>

      <Td>
        <Badge colorScheme={badgeColorScheme}>{item.paramsStateText}</Badge>
      </Td>

      <Td>
        <Flex
          sx={{ gap: 1, placeContent: 'end', visibility: buttonsVisible ? 'visible' : 'hidden' }}
        >
          {false && (
            <>
              <Button
                size="sm"
                colorScheme="primary"
                data-tooltip-content="Odešle nabídku na klientův e-mail."
                onClick={onOpen}
              >
                Odeslat klientovi
              </Button>

              <Modal blockScrollOnMount={false} isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>Odeslat e-mail klientovi?</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    <Text>Přejete si odelsat e-mail klientovi?</Text>
                  </ModalBody>

                  <ModalFooter>
                    <Flex sx={{ gap: 2 }}>
                      <Button variant="outline" onClick={onClose}>
                        Zrušit
                      </Button>
                      <Button>Odeslat</Button>
                    </Flex>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </>
          )}

          <Button variant="outline" size="sm" onClick={() => navigate(navigateTarget)}>
            <Edit />
            Upravit
          </Button>

        </Flex>
      </Td>
    </Tr>
  )
}
